import styles from "./OrganisationPersonalInfo.module.scss";
import { useContext, useEffect, useState } from "react";
import { Button, TextField } from "@mui/material";
import { Dropzone } from "../Dropzone/Dropzone";
import { AuthContext } from "../../context/AuthContext";
import { InputAddress } from "../InputAddress/InputAddress";
import { fetcher } from "../../utils/fetcher";
import { useAlert } from "../../context/AlertContext";
import { FileWithPreview } from "../../types/common";

interface OrganisationDataForm {
  name: string;
  ic_dic: string;
  hq_address: string;
  website_link: string;
  organisation_info: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
}

export const OrganisationPersonalInfo = () => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { currentUser, editUser, fetchOrganisation } = authContext;
  const { setAlert } = useAlert();

  const [editMode, setEditMode] = useState(false);
  const [handleAbortFiles, setHandleAbortFiles] = useState(false);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [organisationData, setOrganisationData] =
    useState<OrganisationDataForm>({
      name: (currentUser?.userData as any)?.name || "",
      ic_dic: (currentUser?.userData as any)?.ic_dic || "",
      hq_address: (currentUser?.userData as any)?.hq_address || "",
      website_link: (currentUser?.userData as any)?.website_link || "",
      organisation_info:
        (currentUser?.userData as any)?.organisation_info || "",
      contact_person: (currentUser?.userData as any)?.contact_person || "",
      contact_email: (currentUser?.userData as any)?.contact_email || "",
      contact_phone: (currentUser?.userData as any)?.contact_phone || "",
    });

  const minTextLength = 100;
  const remainingCharacters = Math.max(
    0,
    minTextLength - (organisationData.organisation_info?.length || 0),
  );

  useEffect(() => {
    if (currentUser) {
      const userData = currentUser.userData as any;
      setOrganisationData({
        name: userData?.name || "",
        ic_dic: userData?.ic_dic || "",
        hq_address: userData?.hq_address || "",
        website_link: userData?.website_link || "",
        organisation_info: userData?.organisation_info || "",
        contact_person: userData?.contact_person || "",
        contact_email: userData?.contact_email || "",
        contact_phone: userData?.contact_phone || "",
      });
    }
  }, [currentUser]);

  const handleEditConfirm = () => {
    if (editMode) {
      // Convert ic_dic to integer or null
      const dataToSend = {
        ...organisationData,
        ic_dic: organisationData.ic_dic
          ? parseInt(organisationData.ic_dic, 10)
          : null,
      };
      editUser(dataToSend, "organisation");
      setEditMode(false);
      return;
    }
    setEditMode(true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { id, value } = e.target;
    setOrganisationData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleEditCancel = () => {
    const userData = currentUser?.userData as any;
    setOrganisationData({
      name: userData?.name || "",
      ic_dic: userData?.ic_dic || "",
      hq_address: userData?.hq_address || "",
      website_link: userData?.website_link || "",
      organisation_info: userData?.organisation_info || "",
      contact_person: userData?.contact_person || "",
      contact_email: userData?.contact_email || "",
      contact_phone: userData?.contact_phone || "",
    });
    setEditMode(false);
    setHandleAbortFiles(true);
  };

  const handleImageUpload = async (file: File | undefined) => {
    if (!file || !currentUser) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await fetcher({
        url: `/organisation/upload-picture/${currentUser.userId}`,
        method: "POST",
        body: formData,
        noContentType: true,
      });
      fetchOrganisation(currentUser.userId);
      setFiles([]);
    } catch (error) {
      setAlert(
        true,
        "error",
        "Nahrání obrázku se nezdařilo, zkuste to prosím znovu později.",
      );
      console.error("Error uploding image:", error);
    }
  };

  const handleImageDelete = async () => {
    if (!currentUser) return;

    try {
      await fetcher({
        url: `/organisation/delete-picture/${currentUser.userId}`,
        method: "DELETE",
      });
      fetchOrganisation(currentUser.userId);
    } catch (error) {
      setAlert(
        true,
        "error",
        "Smazání obrázku se nezdařilo, zkuste to prosím znovu později.",
      );
      console.error("Error deleting image:", error);
    }
  };

  return (
    <div className={styles.personalInfo}>
      <div className={styles.form}>
        <TextField
          id="name"
          type="text"
          label="Název organizace"
          variant="outlined"
          size="small"
          fullWidth
          value={organisationData.name}
          onChange={(e) => handleChange(e)}
          disabled={!editMode}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          id="ic_dic"
          type="number"
          label="IČ / DIČ"
          variant="outlined"
          size="small"
          fullWidth
          value={organisationData.ic_dic}
          onChange={(e) => handleChange(e)}
          disabled={!editMode}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <InputAddress
          editMode={editMode}
          address={{ address_text: organisationData.hq_address }}
          onAddressChange={(value) =>
            setOrganisationData((prevData) => ({
              ...prevData,
              hq_address: value || "",
            }))
          }
        />
        <TextField
          id="website_link"
          type="text"
          label="Webová stránka"
          variant="outlined"
          size="small"
          fullWidth
          value={organisationData.website_link}
          onChange={(e) => handleChange(e)}
          disabled={!editMode}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          id="contact_person"
          type="text"
          label="Koordinátor dobrovolnictví"
          variant="outlined"
          size="small"
          fullWidth
          value={organisationData.contact_person}
          onChange={(e) => handleChange(e)}
          disabled={!editMode}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          id="contact_phone"
          type="text"
          label="Telefon koordinátora"
          variant="outlined"
          size="small"
          fullWidth
          value={organisationData.contact_phone}
          onChange={(e) => handleChange(e)}
          disabled={!editMode}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          id="contact_email"
          type="email"
          label="Email koordinátora"
          variant="outlined"
          size="small"
          fullWidth
          value={organisationData.contact_email}
          onChange={(e) => handleChange(e)}
          disabled={!editMode}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <div className={styles.flex}>
          {editMode && (
            <Button variant="outlined" onClick={() => handleEditCancel()}>
              Zrušit
            </Button>
          )}
          <Button variant="contained" onClick={() => handleEditConfirm()}>
            {editMode ? "Potvrdit" : "Upravit údaje"}
          </Button>
        </div>
      </div>
      <div className={styles.profileImage}>
        <p className={styles.subtitle}>Logo organizace</p>
        <div className={styles.flex}>
          {!(currentUser?.userData as any)?.logo && (
            <Dropzone
              handleAbort={handleAbortFiles}
              files={files}
              setFiles={setFiles}
            />
          )}
          {(currentUser?.userData as any)?.logo && (
            <div className={styles.imageContainer}>
              <button className={styles.delete} onClick={handleImageDelete}>
                Odstranit
              </button>
              <img
                src={(currentUser?.userData as any)?.logo}
                alt="Logo organizace"
                className={styles.image}
              />
            </div>
          )}
        </div>
        {!(currentUser?.userData as any)?.logo && (
          <Button
            variant="contained"
            onClick={() => handleImageUpload(files[0])}
            disabled={!files.length}
            style={{ marginTop: 8, marginBottom: 40 }}
          >
            Nahrát obrázek
          </Button>
        )}
        <TextField
          id="organisation_info"
          label="Popis činnosti"
          variant="outlined"
          placeholder="Popis organizace (min. 100 znaků)"
          fullWidth
          multiline
          rows={4}
          value={organisationData.organisation_info}
          onChange={(e) => handleChange(e)}
          disabled={!editMode}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText={
            (organisationData.organisation_info?.length || 0) < minTextLength
              ? `Zadejte ještě alespoň ${remainingCharacters} znaků.`
              : "Minimální počet znaků splněn."
          }
          className={styles.description}
        />
      </div>
    </div>
  );
};
